/**
 * commandCenter 自主模式子模块
 *
 * 管理自主模式的配置、任务统计和最近事件。
 */
import { getProjectId, logger } from '@/utils'
import { computed, ref } from 'vue'
import type { AutonomyConfig, AutonomyEvent, AutonomyTaskStats } from './types'

/**
 * 创建自主模式子模块的状态和操作
 */
export function useAutonomySlice() {
  // ===== State =====
  const autonomyConfig = ref<AutonomyConfig | null>(null)

  const isAutonomyEnabled = computed(() => {
    const level = autonomyConfig.value?.level
    return level === 'L3' || level === 'L4' || level === 'L5'
  })

  const autonomyTaskStats = ref<AutonomyTaskStats>({
    running: 0,
    pending: 0,
    failed: 0,
    total_today: 0,
  })

  const autonomyRecentEvents = ref<AutonomyEvent[]>([])

  // ===== Actions =====

  async function fetchAutonomyConfig() {
    try {
      const { getAutonomyConfig } = await import('@/api/autonomy')
      const config = await getAutonomyConfig(getProjectId())
      autonomyConfig.value = {
        level: config.level,
        max_concurrent_tasks: config.max_concurrent_tasks,
        max_daily_llm_calls: config.max_daily_llm_calls,
      }
    } catch (error) {
      logger.warn('[CommandCenter] 加载自主模式配置失败:', error)
    }
  }

  function updateAutonomyTaskStats(stats: AutonomyTaskStats) {
    autonomyTaskStats.value = stats
  }

  function addAutonomyEvent(event: AutonomyEvent) {
    autonomyRecentEvents.value.unshift(event)
    // 限制最多 20 条
    if (autonomyRecentEvents.value.length > 20) {
      autonomyRecentEvents.value = autonomyRecentEvents.value.slice(0, 20)
    }
  }

  return {
    // State
    autonomyConfig,
    isAutonomyEnabled,
    autonomyTaskStats,
    autonomyRecentEvents,
    // Actions
    fetchAutonomyConfig,
    updateAutonomyTaskStats,
    addAutonomyEvent,
  }
}
